<!--
/*
 * Компонент TransactionCard — карточка одной транзакции
 *
 * Основные функции:
 * - Отображение данных транзакции: ID, userID, сумма, период, метод, даты
 * - Кнопки копирования ID транзакции и User ID
 * - Статус оплаты через v-chip
 * - Опциональное скрытие User ID (при использовании внутри карточки пользователя)
 */
-->
<script setup lang="ts">
// Composable для буфера обмена — @vueuse/core
import { computed } from 'vue'
import { useClipboard } from '@vueuse/core'
import { useDisplay } from 'vuetify'
// Стор обратной связи — тост-уведомления
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
// Тип транзакции из proto-стабов
import type { Transaction } from '@vpn-tg/vue-client-grpc/proto/transaction/transaction-endpoint'
// Перечисление статусов оплаты — для условия видимости кнопки отмены
import { PaymentStatus } from '@vpn-tg/vue-client-grpc/proto/enums/transactionStatus'
// Хелперы форматирования транзакций
import {
  getStatusLabel,
  getStatusColor,
  getPaymentMethodLabel,
  getCurrencyLabel,
  formatTransactionTimestamp,
  getTimeStatus,
} from '@/modules/admin/utils/transactionHelpers'

// Интерфейс пропсов
interface ITransactionCardProps {
  /** Данные транзакции */
  transaction: Transaction
  /** Скрыть строку User ID (при использовании внутри карточки пользователя) */
  hideUserID?: boolean
  /** Показать кнопку отмены (только для последней активной транзакции в цепочке) */
  showCancel?: boolean
}

// Пропсы с дефолтами
const props = withDefaults(defineProps<ITransactionCardProps>(), {
  hideUserID: false,
  showCancel: false,
})

// Событие отмены или переноса транзакции
const emit = defineEmits<{
  cancel: [transactionId: string]
  move: [transactionId: string]
}>()

// Стор обратной связи
const feedbackStore = useFeedbackStore()
// Composable для копирования в буфер
const { copy } = useClipboard()
// Определение размера экрана
const { mobile } = useDisplay()

/**
 * Копирует значение в буфер обмена и показывает тост
 *
 * @param value — значение для копирования
 * @param label — название поля для тоста
 */
const copyToClipboard = async (value: string, label: string) => {
  await copy(value)
  feedbackStore.showToast({ type: 'info', message: `${label} скопирован` })
}

// Временной статус текущей транзакции — вычисляется один раз
const timeStatus = computed(() => getTimeStatus(props.transaction))

// Цвет карточки — прошлые завершённые приглушаются
const cardColor = computed(() => {
  if (props.transaction.paymentStatus === PaymentStatus.PaymentCompleted && timeStatus.value.value === 'past') {
    return 'disabled'
  }
  return 'surface'
})
</script>

<template>
  <v-card variant="flat" :color="cardColor" class="pa-4">
    <div class="d-flex flex-column ga-2">
      <!-- ID транзакции и статус -->
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center ga-2 truncate-container">
          <span class="text-caption text-medium-emphasis text-truncate">{{ transaction.id }}</span>
          <VIconBtn
            icon="ri-file-copy-line"
            variant="text"
            size="small"
            @click="copyToClipboard(transaction.id, 'ID транзакции')"
          />
        </div>
        <!-- Для завершённых — временной чип (Текущая/Будущая/Прошлая), для остальных — статус оплаты -->
        <v-chip
          v-if="transaction.paymentStatus === PaymentStatus.PaymentCompleted && timeStatus.label"
          :color="timeStatus.color"
          size="small"
          variant="tonal"
        >
          {{ timeStatus.label }}
        </v-chip>
        <v-chip
          v-else
          :color="getStatusColor(transaction.paymentStatus)"
          size="small"
          variant="tonal"
        >
          {{ getStatusLabel(transaction.paymentStatus) }}
        </v-chip>
      </div>

      <div class="d-flex flex-column ga-2">
        <!-- User ID (скрывается при hideUserID) -->
        <div v-if="!hideUserID" class="d-flex align-center justify-space-between">
          <span class="text-body-2 text-medium-emphasis">User ID</span>
          <div class="d-flex align-center ga-2 truncate-container">
            <span class="text-body-2 text-truncate">{{ transaction.userID }}</span>
            <VIconBtn
              icon="ri-file-copy-line"
              variant="text"
              size="small"
              @click="copyToClipboard(transaction.userID, 'User ID')"
            />
          </div>
        </div>
        <!-- Сумма -->
        <div class="d-flex justify-space-between">
          <span class="text-body-2 text-medium-emphasis">Сумма</span>
          <span class="text-body-2">{{ transaction.price }} {{ getCurrencyLabel(transaction.currency) }}</span>
        </div>
        <!-- Период -->
        <div class="d-flex justify-space-between">
          <span class="text-body-2 text-medium-emphasis">Период</span>
          <span class="text-body-2">{{ transaction.durationDays }} дн.</span>
        </div>
        <!-- Метод оплаты -->
        <div class="d-flex justify-space-between">
          <span class="text-body-2 text-medium-emphasis">Метод</span>
          <span class="text-info">{{ getPaymentMethodLabel(transaction.paymentMethod) }}</span>
        </div>
        <!-- Дата создания -->
        <div class="d-flex justify-space-between">
          <span class="text-body-2 text-medium-emphasis">Создана</span>
          <span class="text-body-2">{{ formatTransactionTimestamp(transaction.createdAt) }}</span>
        </div>
        <!-- Начало (опционально) -->
        <div v-if="transaction.dateTimeStart" class="d-flex justify-space-between">
          <span class="text-body-2 text-medium-emphasis">Начало</span>
          <span class="text-body-2">{{ formatTransactionTimestamp(transaction.dateTimeStart) }}</span>
        </div>
        <!-- Окончание (опционально) -->
        <div v-if="transaction.dateTimeEnd" class="d-flex justify-space-between">
          <span class="text-body-2 text-medium-emphasis">Окончание</span>
          <span class="text-body-2">{{ formatTransactionTimestamp(transaction.dateTimeEnd) }}</span>
        </div>
        <!-- Тариф ID (опционально) -->
        <div v-if="transaction.tariffID" class="d-flex justify-space-between">
          <span class="text-body-2 text-medium-emphasis">Тариф ID</span>
          <span class="text-body-2">{{ transaction.tariffID }}</span>
        </div>
      </div>

      <!-- Кнопки управления транзакцией -->
      <div :class="['d-flex ga-2 mt-2', { 'flex-column': mobile }]">
        <v-btn
          v-if="showCancel"
          color="error"
          variant="outlined"
          class="flex-grow-1"
          prepend-icon="ri-close-circle-line"
          @click="emit('cancel', transaction.id)"
        >
          Отменить
        </v-btn>

        <v-btn
          color="primary"
          variant="outlined"
          class="flex-grow-1"
          prepend-icon="ri-user-shared-line"
          @click="emit('move', transaction.id)"
        >
          Перенести
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
/* min-width: 0 + max-width: 60% для агрессивного обрезания ID на мобилке */
.truncate-container {
  min-width: 0;
  max-width: 40%;
}
</style>
